import type { FC, ReactNode } from "react";

/**
 * 「ブラックリストとは？」ツールチップの本文。3 項目の箇条書き。
 * 一覧の新規作成フォームと詳細画面で同じ文面を使うため、ここで 1 箇所だけ持つ。
 */
export const BLACKLIST_TOOLTIP_ITEMS = [
  "このグループの企業から届いたメールは、システムに登録されません",
  "AI による解析も行わないので、その分の費用がかかりません",
  "登録済みのメールは 5〜6 日かけて順に消えます",
] as const;

/** ``data-tip`` に渡す箇条書き文字列。項目区切りは改行。 */
export const BLACKLIST_TOOLTIP_TEXT = BLACKLIST_TOOLTIP_ITEMS.map(
  item => `・${item}`,
).join("\n");

/**
 * ツールチップ吹き出しの Tailwind クラス。
 * daisyUI の tooltip は ``:before`` の ``content: attr(data-tip)`` で本文を描画するため、
 * ``data-tip`` に ``\n`` を入れるだけでは改行されない。``:before`` 自身に
 * ``white-space: pre-line`` を当てる必要がある (daisyUI v4 の既定は ``white-space: normal``)。
 * 併せて箇条書きが読みやすいよう左寄せにし、狭い画面で吹き出しが画面外へはみ出さないよう
 * 吹き出しの左端をリンクの左端に合わせ (``left-0`` + ``transform-none``)、幅を画面幅で抑える。
 */
export const BLACKLIST_TOOLTIP_CLASS =
  "tooltip tooltip-bottom mt-2 block w-fit before:left-0 before:max-w-[min(30rem,calc(100vw-5rem))] before:transform-none before:whitespace-pre-line before:text-left";

/** 理由欄のラベル / プレースホルダ。 */
const REASON_LABEL = "ブラックリストにする理由（任意）";

export type BlacklistFieldsProps = {
  /** ブラックリスト指定の ON/OFF。 */
  isBlacklisted: boolean;
  /** 理由欄の値。 */
  reason: string;
  /** チェックボックスの変更ハンドラ。 */
  onIsBlacklistedChange: (isBlacklisted: boolean) => void;
  /** 理由欄の変更ハンドラ。 */
  onReasonChange: (reason: string) => void;
  /**
   * 理由欄の右に並べる要素 (詳細画面では「保存」ボタン)。
   * 渡したときだけ理由欄と横並びの flex コンテナを挟む。
   */
  reasonActionSlot?: ReactNode;
};

/**
 * ブラックリスト指定の入力欄 (チェックボックス + 常時表示の警告 + ツールチップ + 理由欄)。
 *
 * 一覧の新規作成フォームと詳細画面のブラックリスト設定 section で、文言・クラスとも
 * 完全に同じ UI を出すため、ここに 1 箇所だけ持つ。囲みの ``<div>`` / ``<section>`` と
 * 見出しは画面ごとに違うので呼び出し側に残している。
 * @param {object} props プロパティ
 * @param {boolean} props.isBlacklisted ブラックリスト指定の ON/OFF
 * @param {string} props.reason 理由欄の値
 * @param {Function} props.onIsBlacklistedChange チェックボックスの変更ハンドラ
 * @param {Function} props.onReasonChange 理由欄の変更ハンドラ
 * @param {ReactNode} [props.reasonActionSlot] 理由欄の右に並べる要素 (詳細画面では保存ボタン)
 * @return {JSX.Element} 入力欄
 */
export const BlacklistFields: FC<BlacklistFieldsProps> = ({
  isBlacklisted,
  reason,
  onIsBlacklistedChange,
  onReasonChange,
  reasonActionSlot,
}) => {
  const reasonTextarea = (
    <textarea
      aria-label={REASON_LABEL}
      placeholder={REASON_LABEL}
      maxLength={255}
      disabled={!isBlacklisted}
      value={reason}
      onChange={e => onReasonChange(e.target.value)}
      className="textarea textarea-bordered textarea-sm w-full md:w-96"
      rows={2}
    />
  );

  return (
    <>
      <label className="flex w-fit cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          className="checkbox checkbox-sm"
          checked={isBlacklisted}
          onChange={e => onIsBlacklistedChange(e.target.checked)}
        />
        <span className="text-sm">このグループをブラックリストにする</span>
      </label>
      <div className="alert alert-warning mt-2 w-fit py-2">
        <span className="text-xs">
          このチェックが入っているグループのメールはシステムに登録されなくなります。
        </span>
      </div>
      <div
        className={BLACKLIST_TOOLTIP_CLASS}
        data-testid="blacklist-tooltip"
        data-tip={BLACKLIST_TOOLTIP_TEXT}
      >
        <span className="link link-primary text-xs">ブラックリストとは？</span>
      </div>
      <div className="mt-3">
        {reasonActionSlot ? (
          <div className="flex items-start gap-2">
            {reasonTextarea}
            {reasonActionSlot}
          </div>
        ) : (
          reasonTextarea
        )}
      </div>
    </>
  );
};
